import React, { useState, useEffect } from 'react';
import { Table, Spinner, Alert, Button, Badge, Form, Row, Col, Pagination } from 'react-bootstrap';
import { FaFileInvoice, FaFilter, FaSearch, FaDownload, FaCalendarAlt, FaRupeeSign, FaDollarSign } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../CSS/AdminPaymentManagement.css';

const AdminPaymentManagement = () => {
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [paymentsPerPage] = useState(15);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [exporting, setExporting] = useState(false);
  const [revenueTotals, setRevenueTotals] = useState({ usd: 0, inr: 0 });

  const navigate = useNavigate();

  const services = [
    'USA Company Formation',
    'Tax and Form Return',
    'Pay-As-You-Go',
    'Bookkeeping',
    'CFO Advisory'
  ];

  useEffect(() => {
    const fetchRevenueTotals = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/payment/revenue-totals`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
        });
        setRevenueTotals(response.data);
      } catch (error) {
        console.error('Error fetching revenue totals:', error);
      }
    };

    fetchRevenueTotals();
  }, []);

  useEffect(() => {
    const fetchPayments = async () => {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        navigate('/admin-login');
        return;
      }

      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/payment/all-payments`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPayments(response.data);
        setFilteredPayments(response.data);
      } catch (error) {
        setError(error.response?.data?.message || error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPayments();
  }, [navigate]);

  useEffect(() => {
    filterPayments();
  }, [payments, searchQuery, statusFilter, serviceFilter, dateFilter, startDate, endDate]);

  const filterPayments = () => {
    let filtered = [...payments];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(payment =>
        payment.orderId.toLowerCase().includes(query) ||
        payment.paymentId?.toLowerCase().includes(query) ||
        payment.userDetails?.fullName?.toLowerCase().includes(query) ||
        payment.userDetails?.email?.toLowerCase().includes(query)
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(payment => payment.status === statusFilter);
    }

    // Apply service filter
    if (serviceFilter !== 'all') {
      filtered = filtered.filter(payment => payment.service === serviceFilter);
    }

    // Apply date filter
    if (dateFilter === 'today') {
      const today = new Date().toISOString().split('T')[0];
      filtered = filtered.filter(payment =>
        new Date(payment.createdAt).toISOString().split('T')[0] === today
      );
    } else if (dateFilter === 'thisMonth') {
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();

      filtered = filtered.filter(payment => {
        const paymentDate = new Date(payment.createdAt);
        return (
          paymentDate.getMonth() === currentMonth &&
          paymentDate.getFullYear() === currentYear
        );
      });
    } else if (dateFilter === 'custom' && startDate && endDate) {
      filtered = filtered.filter(payment => {
        const paymentDate = new Date(payment.createdAt).getTime();
        const start = new Date(startDate).getTime();
        const end = new Date(endDate).getTime();
        return paymentDate >= start && paymentDate <= end;
      });
    }

    setFilteredPayments(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const calculateTotalRevenue = (payments, currency) => {
    return payments.reduce((sum, payment) => {
      if (payment.status !== 'completed') return sum;

      if (currency === 'INR' && payment.currency === 'INR') {
        return sum + payment.paidAmount;
      } else if (currency === 'USD' && payment.currency === 'USD') {
        return sum + payment.paidAmount;
      }
      return sum;
    }, 0);
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const formatCurrency = (amount, currency) => {
    const symbol = currency === 'INR' ? '₹' : '$';
    const formattedAmount = new Intl.NumberFormat('en-IN', {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2
    }).format(amount);
    return `${symbol}${formattedAmount}`;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge bg="success" className="admin-payment-status-badge">Completed</Badge>;
      case 'failed':
        return <Badge bg="danger" className="admin-payment-status-badge">Failed</Badge>;
      default:
        return <Badge bg="warning" text="dark" className="admin-payment-status-badge">Created</Badge>;
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      // Validate custom date range if selected
      if (dateFilter === 'custom' && (!startDate || !endDate)) {
        setError('Please select both start and end dates for custom range');
        return;
      }

      // Calculate dates based on filter
      let exportStartDate, exportEndDate;

      if (dateFilter === 'today') {
        const today = new Date().toISOString().split('T')[0];
        exportStartDate = today;
        exportEndDate = today;
      } else if (dateFilter === 'thisMonth') {
        const now = new Date();
        exportStartDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
        exportEndDate = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
      } else if (dateFilter === 'custom') {
        exportStartDate = startDate;
        exportEndDate = endDate;
      }

      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/payment/export-payments`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          status: statusFilter !== 'all' ? statusFilter : undefined,
          service: serviceFilter !== 'all' ? serviceFilter : undefined,
          startDate: exportStartDate,
          endDate: exportEndDate
        },
        responseType: 'blob'
      });

      // Create filename with date range if applicable
      let filename = 'payments_export';
      if (exportStartDate && exportEndDate) {
        filename += `_${exportStartDate}_to_${exportEndDate}`;
      }
      filename += '.csv';

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);

    } catch (error) {
      setError(error.response?.data?.message || error.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  // Pagination logic
  const indexOfLastPayment = currentPage * paymentsPerPage;
  const indexOfFirstPayment = indexOfLastPayment - paymentsPerPage;
  const currentPayments = filteredPayments.slice(indexOfFirstPayment, indexOfLastPayment);
  const totalPages = Math.ceil(filteredPayments.length / paymentsPerPage);

  if (loading) {
    return (
      <div className="admin-payment-loading-container">
        <Spinner animation="border" role="status" className="admin-payment-spinner">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger" className="admin-payment-error-alert">{error}</Alert>;
  }

  return (
    <div className="admin-payment-management-container">
      <div className="admin-payment-header">
        <h2 className="admin-payment-title">Payment Management</h2>
        <div className="admin-payment-header-divider"></div>
      </div>

      {/* Filters Section */}
      <div className="admin-payment-filters-section">
        <Row className="admin-payment-filter-row">
          <Col md={3} className="admin-payment-filter-col">
            <Form.Group className="admin-payment-search-group">
              <Form.Label className="admin-payment-filter-label"><FaSearch className="admin-payment-filter-icon" /> Search Payments</Form.Label>
              <Form.Control
                type="text"
                placeholder="Search by order ID, payment ID, or name"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="admin-payment-search-input"
              />
            </Form.Group>
          </Col>

          <Col md={2} className="admin-payment-filter-col">
            <Form.Group className="admin-payment-filter-group">
              <Form.Label className="admin-payment-filter-label"><FaFilter className="admin-payment-filter-icon" /> Status</Form.Label>
              <Form.Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="admin-payment-filter-select"
              >
                <option value="all">All Statuses</option>
                <option value="created">Created</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
              </Form.Select>
            </Form.Group>
          </Col>

          <Col md={2} className="admin-payment-filter-col">
            <Form.Group className="admin-payment-filter-group">
              <Form.Label className="admin-payment-filter-label"><FaFilter className="admin-payment-filter-icon" /> Service</Form.Label>
              <Form.Select
                value={serviceFilter}
                onChange={(e) => setServiceFilter(e.target.value)}
                className="admin-payment-filter-select"
              >
                <option value="all">All Services</option>
                {services.map(service => (
                  <option key={service} value={service}>{service}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          <Col md={3} className="admin-payment-filter-col">
            <Form.Group className="admin-payment-filter-group">
              <Form.Label className="admin-payment-filter-label"><FaCalendarAlt className="admin-payment-filter-icon" /> Date Range</Form.Label>
              <Form.Select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="admin-payment-filter-select"
              >
                <option value="all">All Dates</option>
                <option value="today">Today</option>
                <option value="thisMonth">This Month</option>
                <option value="custom">Custom Range</option>
              </Form.Select>
            </Form.Group>

            {dateFilter === 'custom' && (
              <div className="admin-payment-date-range-inputs">
                <Form.Control
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="admin-payment-date-input"
                />
                <Form.Control
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="admin-payment-date-input"
                />
              </div>
            )}
          </Col>

          <Col md={2} className="admin-payment-filter-col admin-payment-export-col">
            <Button
              variant="primary"
              onClick={handleExportCSV}
              disabled={exporting || filteredPayments.length === 0}
              className="admin-payment-export-btn"
            >
              {exporting ? 'Exporting...' : <><FaDownload className="admin-payment-export-icon" /> Export CSV</>}
            </Button>
          </Col>
        </Row>
      </div>

      {/* Summary Cards */}
      <div className="admin-payment-summary-cards">
        <Row>
          <Col md={3} className="admin-payment-summary-col">
            <div className="admin-payment-summary-card admin-payment-total-card">
              <h5 className="admin-payment-summary-title">Total Payments</h5>
              <h3 className="admin-payment-summary-value">{payments.length}</h3>
            </div>
          </Col>
          <Col md={3} className="admin-payment-summary-col">
            <div className="admin-payment-summary-card admin-payment-completed-card">
              <h5 className="admin-payment-summary-title">Completed</h5>
              <h3 className="admin-payment-summary-value">{payments.filter(p => p.status === 'completed').length}</h3>
            </div>
          </Col>
          <Col md={3} className="admin-payment-summary-col">
            <div className="admin-payment-summary-card admin-payment-revenue-card">
              <h5 className="admin-payment-summary-title">Total Revenue (USD)</h5>
              <h3 className="admin-payment-summary-value">
                <FaDollarSign className="admin-payment-currency-icon" />
                {formatCurrency(calculateTotalRevenue(payments, 'USD'), 'USD').substring(1)}
              </h3>
            </div>
          </Col>
          <Col md={3} className="admin-payment-summary-col">
            <div className="admin-payment-summary-card admin-payment-revenue-inr-card">
              <h5 className="admin-payment-summary-title">Total Revenue (INR)</h5>
              <h3 className="admin-payment-summary-value">
                <FaRupeeSign className="admin-payment-currency-icon" />
                {formatCurrency(calculateTotalRevenue(payments, 'INR'), 'INR').substring(1)}
              </h3>
            </div>
          </Col>
        </Row>
      </div>

      {/* Payments Table */}
      <div className="admin-payment-table-container">
        <Table striped bordered hover className="admin-payment-table">
          <thead className="admin-payment-table-header">
            <tr>
              <th className="admin-payment-table-th">Order ID</th>
              <th className="admin-payment-table-th">Payment ID</th>
              <th className="admin-payment-table-th">Customer</th>
              <th className="admin-payment-table-th">Service</th>
              <th className="admin-payment-table-th">Amount</th>
              <th className="admin-payment-table-th">Date</th>
              <th className="admin-payment-table-th">Status</th>
              <th className="admin-payment-table-th">Location</th>
              {/* <th className="admin-payment-table-th">GSTIN</th> */}
              <th className="admin-payment-table-th">Invoice</th>
            </tr>
          </thead>
          <tbody className="admin-payment-table-body">
            {currentPayments.length > 0 ? (
              currentPayments.map(payment => (
                <tr key={payment._id} className="admin-payment-table-row">
                  <td className="admin-payment-table-td">{payment.orderId}</td>
                  <td className="admin-payment-table-td">{payment.paymentId || '-'}</td>
                  <td className="admin-payment-table-td">
                    <div className="admin-payment-customer-name">
                      {payment.userDetails?.fullName || 'N/A'}
                    </div>
                    {payment.userDetails?.email && (
                      <div className="admin-payment-customer-email">{payment.userDetails.email}</div>
                    )}
                  </td>
                  <td className="admin-payment-table-td">{payment.service}</td>
                  <td className="admin-payment-table-td">
                    <div className="admin-payment-amount">
                      {formatCurrency(payment.paidAmount, payment.currency)}
                    </div>
                    {payment.gstApplied && (
                      <div className="admin-payment-gst-badge">incl. GST</div>
                    )}
                  </td>
                  <td className="admin-payment-table-td">{formatDate(payment.createdAt)}</td>
                  <td className="admin-payment-table-td">{getStatusBadge(payment.status)}</td>
                  <td className="admin-payment-table-td">
                    <div className="admin-payment-location">
                      {payment.location === 'india' ? 'India' : 'International'}
                    </div>
                    {payment.state && (
                      <div className="admin-payment-state">{payment.state}</div>
                    )}
                  </td>
                  {/* <td className="admin-payment-table-td">{payment.gstin || '-'}</td> */}
                  <td className="admin-payment-table-td">
                    {payment.invoicePath ? (
                      <Button
                        variant="link"
                        size="sm"
                        className="admin-payment-invoice-btn"
                        onClick={() => window.open(
                          `${process.env.REACT_APP_API_BASE_URL}/api/invoice/download/${payment.invoicePath}`,
                          '_blank'
                        )}
                      >
                        <FaFileInvoice className="admin-payment-invoice-icon" /> View
                      </Button>
                    ) : (
                      <span className="admin-payment-no-invoice">N/A</span>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr className="admin-payment-table-row">
                <td colSpan="9" className="admin-payment-no-results">
                  No payments found matching your criteria
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Pagination */}
      {filteredPayments.length > paymentsPerPage && (
        <div className="admin-payment-pagination-container">
          <Pagination className="admin-payment-pagination">
            <Pagination.Prev
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="admin-payment-pagination-prev"
            />

            {Array.from({ length: totalPages }, (_, i) => {
              if (
                i === 0 ||
                i === totalPages - 1 ||
                (i >= currentPage - 2 && i <= currentPage + 2)
              ) {
                return (
                  <Pagination.Item
                    key={i + 1}
                    active={i + 1 === currentPage}
                    onClick={() => setCurrentPage(i + 1)}
                    className="admin-payment-pagination-item"
                  >
                    {i + 1}
                  </Pagination.Item>
                );
              }
              return null;
            })}

            <Pagination.Next
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="admin-payment-pagination-next"
            />
          </Pagination>
        </div>
      )}

      {/* Count summary */}
      <div className="admin-payment-count-summary">
        Showing {indexOfFirstPayment + 1} to {Math.min(indexOfLastPayment, filteredPayments.length)} of {filteredPayments.length} payments
      </div>
    </div>
  );
};

export default AdminPaymentManagement;